import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import {
  useGetRevenueSettingsHook,
  useGetRevenueStatsHook,
  useUpdateRevenueSettingHook,
} from "../hooks/revenue.hook";
import { RevenueSetting, RevenueType, UpdateRevenueSettingDto } from "../dto/index.dto";
import { formatDateTime } from "../../../shared/utils/format.util";
import { MonthlyReservationsChartComponent } from "../../dashboard/components/monthly-reservations-chart.component";
import { HorizontalBarListComponent } from "../../dashboard/components/horizontal-bar-list.component";

const TYPE_COLORS: Record<string, string> = {
  activity: "#2a78d6",
  restaurant: "#1baf7a",
  accomodation: "#eda100",
  competition: "#008300",
  event: "#4a3aa7",
};

const clampAmount = (value: number) => Math.max(0, value);

interface RowEditValue {
  feeAmount: string;
  displayAmount: string;
}

export const RevenueComponent: React.FC = () => {
  const {
    data: settings,
    isLoading: settingsLoading,
    error: settingsError,
  } = useGetRevenueSettingsHook();
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useGetRevenueStatsHook();
  const updateRateMutation = useUpdateRevenueSettingHook();

  const [editValues, setEditValues] = useState<Record<string, RowEditValue>>(
    {}
  );
  const [rowError, setRowError] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!settings) return;
    setEditValues((prev) => {
      const next = { ...prev };
      settings.forEach((s) => {
        if (!(s.type in next)) {
          next[s.type] = {
            feeAmount: String(s.feeAmount),
            displayAmount: String(s.displayAmount),
          };
        }
      });
      return next;
    });
  }, [settings]);

  const getEditValue = (setting: RevenueSetting): RowEditValue =>
    editValues[setting.type] ?? {
      feeAmount: String(setting.feeAmount),
      displayAmount: String(setting.displayAmount),
    };

  const isDirty = (setting: RevenueSetting) => {
    const edited = getEditValue(setting);
    return (
      edited.feeAmount !== String(setting.feeAmount) ||
      edited.displayAmount !== String(setting.displayAmount)
    );
  };

  const handleFieldChange = (
    type: string,
    field: keyof RowEditValue,
    value: string
  ) => {
    setEditValues((prev) => ({
      ...prev,
      [type]: {
        feeAmount: prev[type]?.feeAmount ?? "0",
        displayAmount: prev[type]?.displayAmount ?? "0",
        [field]: value,
      },
    }));
  };

  const handleSaveRow = (setting: RevenueSetting) => {
    const type = setting.type as RevenueType;
    const edited = getEditValue(setting);
    const fee = clampAmount(Number(edited.feeAmount) || 0);
    const display = clampAmount(Number(edited.displayAmount) || 0);

    const payload: UpdateRevenueSettingDto = {};
    if (fee !== setting.feeAmount) payload.feeAmount = fee;
    if (display !== setting.displayAmount) payload.displayAmount = display;
    if (Object.keys(payload).length === 0) return;

    setRowError((prev) => ({ ...prev, [type]: "" }));
    updateRateMutation.mutate(
      { type, data: payload },
      {
        onSuccess: (updated) => {
          setEditValues((prev) => ({
            ...prev,
            [type]: {
              feeAmount: String(updated.feeAmount),
              displayAmount: String(updated.displayAmount),
            },
          }));
        },
        onError: () =>
          setRowError((prev) => ({
            ...prev,
            [type]: "Erreur lors de l'enregistrement.",
          })),
      }
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Rate settings */}
      <Box
        sx={{
          backgroundColor: "#FFFDF7",
          borderRadius: "12px",
          p: 2,
        }}
      >
        <Typography fontWeight="bold" fontSize="1rem" color="#B4006E" mb={1}>
          Frais par réservation
        </Typography>

        {settingsLoading && (
          <Box p={2} textAlign="center" color="#999" fontSize="0.85rem">
            Chargement...
          </Box>
        )}
        {!settingsLoading && settingsError && (
          <Box p={2} textAlign="center" color="#c0392b" fontSize="0.85rem">
            Erreur lors du chargement des frais.
          </Box>
        )}

        {settings?.map((setting) => {
          const edited = getEditValue(setting);
          const liveTotal =
            (Number(edited.feeAmount) || 0) + (Number(edited.displayAmount) || 0);
          return (
            <Box
              key={setting.type}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1.5}
              sx={{ py: 1.2, borderBottom: "1px solid #f0ede6" }}
            >
              <Box>
                <Typography fontWeight="600" fontSize="0.85rem">
                  {setting.label}
                </Typography>
                <Typography fontSize="0.7rem" color="gray">
                  {setting.updatedAt
                    ? `Modifié le ${formatDateTime(setting.updatedAt)}`
                    : "Jamais modifié"}
                </Typography>
                <Typography fontSize="0.7rem" color="#B4006E" fontWeight="600">
                  Total : {liveTotal.toLocaleString("fr-FR")} € / réservation
                </Typography>
                {rowError[setting.type] && (
                  <Typography fontSize="0.7rem" color="#c0392b">
                    {rowError[setting.type]}
                  </Typography>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <TextField
                  label="Frais de service"
                  type="number"
                  size="small"
                  value={edited.feeAmount}
                  onChange={(e) =>
                    handleFieldChange(setting.type, "feeAmount", e.target.value)
                  }
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">€</InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 150,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: "13px",
                    },
                    "& .MuiInputLabel-root": { fontSize: "12px" },
                  }}
                />
                <TextField
                  label="Frais d'affichage"
                  type="number"
                  size="small"
                  value={edited.displayAmount}
                  onChange={(e) =>
                    handleFieldChange(
                      setting.type,
                      "displayAmount",
                      e.target.value
                    )
                  }
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">€</InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 150,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: "13px",
                    },
                    "& .MuiInputLabel-root": { fontSize: "12px" },
                  }}
                />
                <IconButton
                  size="small"
                  disabled={!isDirty(setting) || updateRateMutation.isPending}
                  onClick={() => handleSaveRow(setting)}
                  sx={{
                    color: isDirty(setting) ? "#B4006E" : "#ccc",
                  }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        })}

        <Typography fontSize="0.7rem" color="gray" mt={1.5}>
          Les nouveaux frais s'appliquent uniquement aux réservations futures —
          les totaux déjà calculés ne sont pas recalculés.
        </Typography>
      </Box>

      {/* Stats */}
      {statsLoading && (
        <Box p={2} textAlign="center" color="#999" fontSize="0.85rem">
          Chargement des statistiques...
        </Box>
      )}
      {!statsLoading && statsError && (
        <Box p={2} textAlign="center" color="#c0392b" fontSize="0.85rem">
          Erreur lors du chargement des statistiques.
        </Box>
      )}
      {stats && (
        <>
          {/* Headline */}
          <Box
            sx={{
              backgroundColor: "#FFFDF7",
              borderRadius: "14px",
              padding: "18px 20px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography
              sx={{ fontSize: "13px", fontWeight: 700, color: "#333", mb: 0.5 }}
            >
              Revenu total
            </Typography>
            <Typography
              sx={{ fontSize: "1.8rem", fontWeight: 700, color: "#2d2d2d" }}
            >
              {stats.totalRevenue.toLocaleString("fr-FR")} €
            </Typography>
          </Box>

          {/* Charts */}
          <Box display="flex" flexWrap="wrap" gap={2}>
            <MonthlyReservationsChartComponent
              data={stats.byMonth.map((m) => ({
                month: m.month,
                label: m.label,
                count: m.total,
              }))}
            />
            <HorizontalBarListComponent
              title="Revenu par catégorie"
              data={stats.byType.map((t) => ({
                key: t.type,
                label: t.label,
                value: t.total,
                color: TYPE_COLORS[t.type] || "#B4006E",
              }))}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
